import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { config } from '../config';
import CircularLoader from './CircularLoader';
import { withImpersonationHeader } from '../utils/impersonation';

interface Session {
  id: string;
  session_id: string;  // Frontend uses session_id
  context_id?: string;  // Backend API field (for compatibility)
  display_name: string;
  description?: string;
  last_url?: string;
  created_at: string;
  updated_at: string;
}

interface AuthData {
  status: string;
  session_name: string;
  platform_url: string;
  cookies: string;
}

const CookiesDashboard: React.FC = () => {
  const { session, user } = useAuth();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [authData, setAuthData] = useState<AuthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchingCookies, setFetchingCookies] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const getAuthHeaders = (): HeadersInit => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }
    
    return withImpersonationHeader(headers);
  };

  const showToastNotification = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 3000);
  };

  const loadSessions = async () => {
    if (!session?.access_token) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(config.getApiUrl('/browser/contexts'), {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        // Map backend context_id to frontend session_id
        const items = (data.contexts || []).map((item: any) => ({
          ...item,
          session_id: item.context_id,  // Map context_id to session_id for frontend use
        }));
        setSessions(items);
        setError(null);
      } else if (response.status === 401) {
        setError('Authentication failed - please log in again');
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        setError(errorData.detail || 'Failed to load sessions');
      }
    } catch (err) {
      console.error('Error loading sessions:', err);
      setError('Network error - failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const fetchCookies = async () => {
    if (!selectedSession || !session?.access_token || !user?.id) {
      return;
    }

    setFetchingCookies(true);
    setError(null);
    setAuthData(null); // Clear existing response

    try {
      const response = await fetch(
        config.getApiUrl(`/browser/context/${selectedSession.session_id}/auth?user_id=${user.id}`),
        {
          headers: getAuthHeaders(),
          credentials: 'include'
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Filter out id-related fields as requested
        const filteredData = {
          status: data.status,
          session_name: data.context_name,
          platform_url: data.platform_url,
          cookies: data.cookies
        };
        setAuthData(filteredData);
      } else if (response.status === 401) {
        setError('Authentication failed - please log in again');
      } else if (response.status === 404) {
        setError('Session not found or access denied');
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        setError(errorData.detail || 'Failed to fetch cookies');
      }
    } catch (err) {
      console.error('Error fetching cookies:', err);
      setError('Network error - failed to fetch cookies');
    } finally {
      setFetchingCookies(false);
    }
  };

  const copyToClipboard = async () => {
    if (!authData) return;

    try {
      await navigator.clipboard.writeText(JSON.stringify(authData, null, 2));
      showToastNotification('✅ Copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      showToastNotification('❌ Failed to copy to clipboard');
    }
  };

  const copySessionId = async (sessionId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row selection
    
    try {
      await navigator.clipboard.writeText(sessionId);
      showToastNotification('✅ Session ID copied!');
    } catch (err) {
      console.error('Failed to copy session ID:', err);
      showToastNotification('❌ Failed to copy session ID');
    }
  };

  // Load sessions when component first mounts and is actually visible
  // (since this component is only rendered when on cookies page)
  useEffect(() => {
    if (session?.access_token) {
      loadSessions();
    }
  }, [session?.access_token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-zinc-950">
        <CircularLoader size="medium" />
      </div>
    );
  }

      return (
      <div className="h-full bg-zinc-950 relative">

      <div className="bg-zinc-950 shadow-sm border-b border-zinc-800 px-6 py-4">
        {/* <h1 className="text-2xl font-semibold text-zinc-200">Cookies Dashboard</h1> */}
        <p className="text-sm text-zinc-400 mt-1">
          Fetch authentication cookies from your saved browser sessions
        </p>
      </div>

      <div className="flex h-[calc(100vh-120px)]">
        {/* Left sidebar - Contexts list (30%) */}
        <div className="w-[30%] bg-zinc-900 border-r border-zinc-800 overflow-y-auto">
          <div className="p-4 border-b border-zinc-800">
            <h2 className="text-lg font-medium text-zinc-200">Saved Sessions</h2>
            <p className="text-sm text-zinc-400 mt-1">
              {sessions.length} session{sessions.length !== 1 ? 's' : ''} available
            </p>
          </div>

          <div className="p-4 space-y-3 pb-8">
            {sessions.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-zinc-500 text-sm">
                  No saved sessions found.
                  <br />
                  Create and save browser sessions from the main chat interface.
                </div>
              </div>
            ) : (
              sessions.map((sess) => (
                <div
                  key={sess.id}
                  onClick={() => setSelectedSession(sess)}
                  className={`
                    p-3 rounded-lg border cursor-pointer transition-all duration-200 relative
                    ${selectedSession?.id === sess.id
                      ? 'border-blue-500 bg-blue-500/10 shadow-sm'
                      : 'border-zinc-700 hover:border-zinc-600 hover:bg-zinc-800/50'
                    }
                  `}
                >
                  {/* Copy Session ID Button */}
                  <button
                    onClick={(e) => copySessionId(sess.session_id, e)}
                    className="absolute top-2 right-2 p-1 rounded text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700/50 transition-colors duration-200"
                    title="Copy Session ID"
                  >
                    <svg 
                      width="14" 
                      height="14" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                  </button>

                  <div className="font-medium text-zinc-200 text-sm pr-6" title={sess.session_id}>
                    {sess.session_id}
                  </div>
                  {(sess.display_name || sess.description) && (
                    <div className="text-xs text-zinc-400 mt-1 pr-6">
                      {sess.display_name || sess.description}
                    </div>
                  )}
                  {sess.last_url && (
                    <div className="text-xs text-blue-400 mt-1 truncate pr-6" title={sess.last_url}>
                      {sess.last_url.length > 60 ? `${sess.last_url.slice(0, 57)}…` : sess.last_url}
                    </div>
                  )}
                  <div className="text-xs text-zinc-500 mt-1">
                    {new Date(sess.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right content area (70%) */}
        <div className="w-[70%] flex flex-col bg-zinc-950 relative">
          {/* Toast Notification - positioned in right pane */}
          {showToast && (
            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-50 animate-in slide-in-from-bottom-2 duration-300">
              <div className="bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 shadow-lg">
                <div className="text-sm text-zinc-200">{toastMessage}</div>
              </div>
            </div>
          )}
          
          <div className="p-6 bg-zinc-950 border-b border-zinc-800">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-medium text-zinc-200">
                  {selectedSession ? selectedSession.display_name : 'Cookie Data'}
                </h2>
                {selectedSession && (
                  <p className="text-sm text-zinc-400 mt-1">
                    Click "Fetch Cookies" to extract authentication data from this session
                  </p>
                )}
              </div>
              
              <button
                onClick={fetchCookies}
                disabled={!selectedSession || fetchingCookies}
                className={`
                  px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200
                  ${!selectedSession || fetchingCookies
                    ? 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-zinc-950'
                  }
                `}
              >
                {fetchingCookies ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin"></div>
                    <span>Fetching...</span>
                  </div>
                ) : (
                  'Fetch Cookies'
                )}
              </button>
            </div>
          </div>

          <div className="flex-1 p-6 pb-16 bg-zinc-950">
            {!selectedSession ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  {/* <div className="text-zinc-500 text-lg mb-2">🍪</div> */}
                  <div className="text-zinc-400">
                    Select a session from the left to fetch its cookies
                  </div>
                </div>
              </div>
            ) : fetchingCookies ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="w-8 h-8 border-2 items-center border-zinc-700 border-t-blue-500 rounded-full animate-spin mb-4"></div>
                  <div className="text-zinc-400">
                    Extracting authentication data...
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">
                    This may take a few moments
                  </div>
                </div>
              </div>
            ) : !authData ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="text-zinc-500 text-lg mb-2">⬆️</div>
                  <div className="text-zinc-400">
                    Click "Fetch Cookies" to retrieve authentication data
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-medium text-zinc-300">Authentication Data</h3>
                  <button
                    onClick={copyToClipboard}
                    className="px-3 py-1 text-xs font-medium text-blue-400 bg-blue-500/10 rounded-md hover:bg-blue-500/20 transition-colors duration-200 border border-blue-500/20"
                  >
                    Copy to Clipboard
                  </button>
                </div>
                
                <textarea
                  value={JSON.stringify(authData, null, 2)}
                  readOnly
                  className="w-full h-full min-h-[300px] p-4 font-mono text-sm bg-zinc-900 border border-zinc-700 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-zinc-300"
                  style={{ fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace' }}
                />
              </div>
            )}

            {error && (
              <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                <div className="text-sm text-red-400">{error}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiesDashboard; 
